import {
  DragDropContext,
  Droppable,
  Draggable
} from 'react-beautiful-dnd';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, TextField, Typography } from '@mui/material';
import { Add, Close, Remove } from '@mui/icons-material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { useState } from 'react';
import { allColumns, ReportType } from './const';
import { showNotification } from './Notification/ToastifyNotification';

const ColumnSection = ({ title, data, newColumns, setNewColumns, id }) => {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>{title}:</Typography>
        {title !== 'Procurement' && (
          <>
            <Link
              color="primary"
              style={{ textDecoration: 'none', fontSize: 14, cursor: 'pointer' }}
              onClick={() => {
                const arr = [...newColumns, ...data];
                const uniqueArr = [
                  ...new Map(arr.map(item => [item.display, item])).values()
                ];
                const uniqueArrWithIds = uniqueArr.map((item) => ({
                  field: item.field,
                  title: item.display,
                  colId: id !== null ? id : ''
                }));
                setNewColumns((prevData) => ([
                  ...prevData,
                  ...uniqueArrWithIds
                ]));
              }}
            >
              Add All
            </Link>
            <Link
              color="secondary"
              style={{ textDecoration: 'none', fontSize: 14, cursor: 'pointer' }}
              onClick={() => {
                const arr = [...newColumns];
                setNewColumns(arr.filter((a) => !data.some((d) => d.field === a.field)));
              }}
            >
              Remove All
            </Link>
          </>
        )}
      </Box>
      {data?.map((item, index) => (
        <Button
          key={index}
          variant={newColumns.findIndex((a) => a.field === item.field) > -1 ? 'contained' : 'outlined'}
          size='small'
          color={newColumns.findIndex((a) => a.field === item.field) > -1 ? "primary" : ""}
          startIcon={newColumns.findIndex((a) => a.field === item.field) > -1 ? <Remove /> : <Add />}
          style={{ margin: 3, borderRadius: 25, fontSize: 11 }}
          onClick={() => {
            const arr = [...newColumns];
            // FIX: was `a.title === item.field` — comparing display title to field key, never matched.
            // Correct: match on a.field === item.field (both are snake_case field keys).
            const idx = arr.findIndex((a) => a.field === item.field);
            if (idx > -1) {
              arr.splice(idx, 1);
            } else {
              arr.push({
                title: item.display,
                field: item.field,
                colId: id !== null ? id : ''
              });
            }
            setNewColumns(arr);
          }}
        >
          {item.title}
        </Button>
      ))}
    </Box>
  );
};

const DynamicTable = ({
  columns,
  setColumns,
  reportType
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allColumn, setAllColumn] = useState([]);
  const [newColumns, setNewColumns] = useState([]);
  const [columnId, setColumnId] = useState(null);

  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;

    if (type === 'COLUMN') {
      const reordered = Array.from(columns);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      setColumns(reordered);
    } else {
      let newColumns = [...columns];

      const sourceColIndex = newColumns.findIndex(c => c.id === source.droppableId);
      const destColIndex = newColumns.findIndex(c => c.id === destination.droppableId);

      const sourceCol = newColumns[sourceColIndex];
      const destCol = newColumns[destColIndex];

      const sourceItems = Array.from(sourceCol.subHeaders);
      const [movedItem] = sourceItems.splice(source.index, 1);

      if (source.droppableId === destination.droppableId) {
        sourceItems.splice(destination.index, 0, movedItem);
        const updatedItems = sourceItems.map((item, index) => ({
          ...item,
          id: `${sourceCol.id}-sub${index + 1}`,
          mainId: sourceCol.id
        }));
        newColumns[sourceColIndex] = { ...sourceCol, subHeaders: updatedItems };
      } else {
        const destItems = Array.from(destCol.subHeaders);
        destItems.splice(destination.index, 0, {
          ...movedItem,
          id: `${destCol.id}-sub${destination.index + 1}`,
          mainId: destCol.id
        });
        const updatedDestItems = destItems.map((item, index) => ({
          ...item,
          id: `${destCol.id}-sub${index + 1}`,
          mainId: destCol.id
        }));
        newColumns[destColIndex] = { ...destCol, subHeaders: updatedDestItems };

        if (sourceItems.length === 0) {
          newColumns.splice(sourceColIndex, 1);
          newColumns = newColumns.map((col, colIndex) => {
            const newColId = `col${colIndex + 1}`;
            const updatedSubHeaders = col.subHeaders.map((sh, shIndex) => ({
              ...sh,
              id: `${newColId}-sub${shIndex + 1}`,
              mainId: newColId
            }));
            return { ...col, id: newColId, subHeaders: updatedSubHeaders };
          });
        } else {
          const updatedSourceItems = sourceItems.map((item, index) => ({
            ...item,
            id: `${sourceCol.id}-sub${index + 1}`,
            mainId: sourceCol.id
          }));
          newColumns[sourceColIndex] = { ...sourceCol, subHeaders: updatedSourceItems };
        }
      }

      setColumns(newColumns);
    }
  };

  const recalculateColumnAndSubHeaderIds = (columns) => {
    return columns.map((col, colIndex) => {
      const newColId = `col${colIndex + 1}`;
      const newSubHeaders = col.subHeaders.map((sh, shIndex) => ({
        ...sh,
        id: `${newColId}-sub${shIndex + 1}`,
        mainId: newColId
      }));
      return { ...col, id: newColId, subHeaders: newSubHeaders };
    });
  };

  const addColumn = () => {
    const seenFields = new Set();
    const newFields = new Set(newColumns.map(col => col.field));

    const updatedColumns = [
      ...columns
        .map(col => {
          const filteredSubHeaders = col.subHeaders.filter(sub => newFields.has(sub.field));
          return filteredSubHeaders.length > 0 ? { ...col, subHeaders: filteredSubHeaders } : null;
        })
        .filter(Boolean),
      ...newColumns
        .filter(a => a.colId === '')
        .map(a => ({
          id: '',
          subHeaders: [(({ colId, ...rest }) => rest)(a)]
        }))
    ]
      .map(col => {
        const uniqueSubHeaders = col.subHeaders.filter(sub => {
          if (!seenFields.has(sub.field)) {
            seenFields.add(sub.field);
            return true;
          }
          return false;
        });
        return uniqueSubHeaders.length > 0 ? { ...col, subHeaders: uniqueSubHeaders } : null;
      })
      .filter(Boolean);

    setColumns(recalculateColumnAndSubHeaderIds(updatedColumns));
  };

  const addSubHeader = (colId) => {
    const updatedColumns = columns.map(col =>
      col.id === colId
        ? {
          ...col,
          subHeaders: [
            ...col.subHeaders.filter(existingSubHeader =>
              !newColumns.some(newSubHeader => newSubHeader.title === existingSubHeader.title)
            ),
            ...newColumns.filter(a => a.colId === colId).map(({ colId, ...rest }) => rest)
          ]
        }
        : col
    );
    if (updatedColumns.find((a) => a.id === colId).subHeaders.length < 5) {
      setColumns(recalculateColumnAndSubHeaderIds(updatedColumns));
    } else {
      showNotification('You can add maximum 4 subheader in coversheet', 'error');
    }
  };

  const removeSubHeader = (colId, subIdToRemove) => {
    const updatedColumns = columns
      .map(col =>
        col.id === colId
          ? { ...col, subHeaders: col.subHeaders.filter(sh => sh.id !== subIdToRemove) }
          : col
      )
      .filter(col => col.subHeaders.length > 0);
    setColumns(recalculateColumnAndSubHeaderIds(updatedColumns));
  };

  const removeColumn = (colId) => {
    const updatedColumns = columns.filter(col => col.id !== colId);
    setColumns(recalculateColumnAndSubHeaderIds(updatedColumns));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '96vw', overflowX: 'auto' }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="all-columns" direction="horizontal" type="COLUMN">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ display: 'flex', minWidth: 'fit-content' }}
            >
              {columns.map((column, colIndex) => (
                <Draggable draggableId={column.id} index={colIndex} key={column.id}>
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        width: 154,
                        flexShrink: 0,
                        fontSize: 14,
                      }}
                    >
                      <Droppable droppableId={column.id} type="SUB">
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {column.subHeaders.map((sub, index) => (
                              <Draggable key={sub.id} draggableId={sub.id} index={index}>
                                {(provided) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      border: '1px solid #999',
                                      borderRadius: 4,
                                      padding: 6,
                                      backgroundColor: '#fff',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <DragHandleIcon fontSize="small" />
                                      {sub.title}
                                    </Box>
                                    {!column.subHeaders.find((a) => ['planned_additions', 'po_additions', 'variance_additions', 'planned_unit_cost', 'po_unit_cost', 'variance_unit_cost'].includes(a.field)) && (
                                      <Close
                                        fontSize="small"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => removeSubHeader(column.id, sub.id)}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1}
                        ml={0.5}
                        mr={0.5}
                        {...provided.dragHandleProps}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          disabled={column.subHeaders.find((a) => [
                            'planned_additions', 'po_additions', 'variance_additions',
                            'planned_unit_cost', 'po_unit_cost', 'variance_unit_cost'
                          ].includes(a.field))}
                          style={{ borderRadius: '50px', fontSize: 10 }}
                          onClick={() => {
                            if (column?.subHeaders?.length < 4) {
                              setAllColumn(allColumns);
                              setNewColumns(columns.flatMap(item =>
                                item.subHeaders.map(subHeader => ({
                                  title: subHeader.title,
                                  field: subHeader.field,
                                  colId: item.id
                                }))
                              ));
                              setColumnId(column.id);
                              setDialogOpen(true);
                            } else {
                              showNotification('You can add maximum 4 subheader in coversheet', 'error');
                            }
                          }}
                        >
                          Add
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          disabled={column.subHeaders.find((a) => ['planned_additions', 'po_additions', 'variance_additions', 'planned_unit_cost', 'po_unit_cost', 'variance_unit_cost'].includes(a.field))}
                          style={{ borderRadius: '50px', fontSize: 10 }}
                          onClick={() => removeColumn(column.id)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<Add />}
                style={{ width: 100, marginLeft: 10, marginTop: 18, height: 38 }}
                onClick={() => {
                  if (reportType === ReportType[13]) {
                    if (columns.length < 6) {
                      setAllColumn(allColumns);
                      setNewColumns(columns.flatMap(item =>
                        item.subHeaders.map(subHeader => ({
                          title: subHeader.title,
                          field: subHeader.field,
                          colId: item.id
                        }))
                      ));
                      setDialogOpen(true);
                    } else {
                      showNotification('You can add maximum 6 column header in coversheet', 'error');
                    }
                  } else if (reportType === ReportType[14]) {
                    if (columns.length < 8) {
                      setAllColumn(allColumns);
                      setNewColumns(columns.flatMap(item =>
                        item.subHeaders.map(subHeader => ({
                          title: subHeader.title,
                          field: subHeader.field,
                          colId: item.id
                        }))
                      ));
                      setDialogOpen(true);
                    } else {
                      showNotification('You can add maximum 8 column header in coversheet', 'error');
                    }
                  } else {
                    setAllColumn(allColumns);
                    setNewColumns(columns.flatMap(item =>
                      item.subHeaders.map(subHeader => ({
                        title: subHeader.title,
                        field: subHeader.field,
                        colId: item.id
                      }))
                    ));
                    setDialogOpen(true);
                  }
                }}
              >
                Add
              </Button>
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog open={dialogOpen} fullWidth maxWidth="sm">
        <DialogTitle>Add Column Header</DialogTitle>
        <Divider />
        <DialogContent style={{ padding: '10px 8px' }}>
          <TextField
            label="Search for a field to add"
            name="search"
            variant="outlined"
            fullWidth
            size='small'
            onChange={(e) => {
              if (e.target.value) {
                const text = e.target.value.toLowerCase();
                const filtered = Object.keys(allColumns).reduce((acc, category) => {
                  const filteredData = allColumns[category].filter((item) =>
                    item.title.toLowerCase().includes(text)
                  );
                  if (filteredData.length > 0) {
                    acc[category] = filteredData;
                  }
                  return acc;
                }, {});
                setAllColumn(filtered);
              } else {
                setAllColumn(allColumns);
              }
            }}
          />
          <Box display="flex" flexDirection="column" gap={2} mt={3}>
            <ColumnSection title="Basic" data={allColumn.basic} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Financial" data={allColumn.financial} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Responsibility" data={allColumn.Responsibility} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Physical" data={allColumn.Physical} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Created By" data={allColumn.CreatedBy} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Electrical" data={allColumn.Electrical} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Mechanical" data={allColumn.Mechanical} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Plumbing" data={allColumn.Plumbing} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Thumbnail" data={allColumn.Thumbnail} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Technical" data={allColumn.Technical} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Procurement" data={allColumn.Procurement} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Action Dates" data={allColumn['Action Dates']} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Asset" data={allColumn.Asset} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Costing/Lifetime" data={allColumn['Costing/Lifetime']} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
            <ColumnSection title="Location" data={allColumn.Location} newColumns={newColumns} setNewColumns={setNewColumns} id={columnId} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => {
              setDialogOpen(false);
              setNewColumns([]);
              setAllColumn([]);
              setColumnId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              if (columnId !== null) {
                addSubHeader(columnId);
              } else {
                addColumn();
              }
              setDialogOpen(false);
              setColumnId(null);
              setNewColumns([]);
              setAllColumn([]);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicTable;